import moment from 'moment';
import {isPlainObject, isEmpty} from 'lodash/lang';
import {capitalize} from 'lodash/string';
import request from '../api/base';
import {MultipleChoiceIcon, SingleChoiceIcon} from '../components/Question/QuestionTypeIcons';
import React from 'react';


const objToQuery = obj => Object.entries(obj)
    .filter((entry) => typeof entry[1] !== 'undefined')
    .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
    .join('&');

const normalizeUrl = url => url.slice(-1) === '?' ? url.slice(0, url.length - 1) : url;

export const buildUrl = (url, query) => normalizeUrl(`${process.env.SERVER_BASE_URL}${url}?${objToQuery(query)}`);

export const toCamelCase = v => v.replace(/\B_[a-z0-9]/g, m => m[1].toUpperCase());

export const decodeURIJSON = json => Object.keys(json).reduce((previous, key) => {
    previous[key] = decodeURIComponent(json[key]);
    return previous;
}, {});

export const buildQueryString = obj => encodeURI(objToQuery(obj));

export const snakeToCamel = data => {
    if (Array.isArray(data)) {
        const ret = [];
        for (let item of data) {
            ret.push(snakeToCamel(item));
        }
        return ret;
    } else if (isPlainObject(data)) {
        let ret = {};
        for (let [k, v] of Object.entries(data)) {
            ret[toCamelCase(k)] = snakeToCamel(v);
        }
        return ret;
    }
    return data;
};

export const toSnakeCase = v => v.replace(/\B[a-z0-9][A-Z]/g, m => m[0] + '_' + m[1].toLowerCase());

export const camelToSnake = data => {
    if (Array.isArray(data)) {
        const ret = [];
        for (let item of data) {
            ret.push(camelToSnake(item));
        }
        return ret;
    } else if (isPlainObject(data)) {
        let ret = {};
        for (let [k, v] of Object.entries(data)) {
            ret[toSnakeCase(k)] = camelToSnake(v);
        }
        return ret;
    }
    return data;
};

export const excludeObjNullValue = data => {
    const ret = {};
    for (let [k, v] of Object.entries(data)) {
        if (v !== null && v !== undefined) {
            ret[k] = v;
        }
    }
    return ret;
};

export const buildUserFullName = (user) => {
    if (!user) {
        return '';
    }
    const firstName = user.firstName || (user.get ? user.get('firstName', '') : '') || '';
    const lastName = user.lastName || (user.get ? user.get('lastName', '') : '') || '';
    if (!firstName && !lastName) {
        return '';
    }
    return `${capitalize(firstName)} ${capitalize(lastName)}`;
};

export const formatDateTime = (str) => isEmpty(str) ? '' : moment(str).utc(str).format('YYYY-MM-DD HH:mm:ss');

export const buildQuestionAnswerGroup = (formValueDict) => {
    const questionAnswerGroupList = [];
    for (let [fieldName, fieldValue] of Object.entries(formValueDict)) {
        if (fieldName.startsWith('question-answer-correct-answer-')) {
            const index = fieldName.replace(/[^0-9]/g, '');
            const answerDict = {
                'correct': fieldValue,
                'content': formValueDict['question-answer-rtef-' + index]
            };
            questionAnswerGroupList.push(answerDict);
        }
    }
    return questionAnswerGroupList;
};

export const calcTableEntriesInfo = (itemsPerPage, currentPage, totalCount) => (
    `Showing ${Math.min((currentPage - 1) * itemsPerPage + 1, totalCount)} ` +
    `to ${Math.min(currentPage * itemsPerPage, totalCount)} ` +
    `of ${totalCount} ${totalCount === 1 ? 'entry' : 'entries'}`
);

export const translateQuestionType = Object.freeze({
    single_choice: <><SingleChoiceIcon/> Single Choice</>,
    multiple_choice: <><MultipleChoiceIcon/> Multiple Choice</>
});

export const formatQuizAnswersForBackend = (quizAnswers) => {
    const quizAnswersList = [];
    for (let [questionId, answerIdsList] of Object.entries(quizAnswers)) {
        quizAnswersList.push({question_id: questionId, answer_ids: answerIdsList});
    }
    return quizAnswersList;
};

export const formatStudentAnswerForBackend = (studentAnswers) => {
    const studentAnswersList = [];
    for (let [questionId, answerIds] of Object.entries(studentAnswers)) {
        studentAnswersList.push({question_id: questionId, answer_ids: answerIds});
    }
    return studentAnswersList;
};

export const buildAnswers = (questions) =>
    questions.map(item => item).reduce(
        (accumulator, currentValue) => {
            if (currentValue._cls === 'MultipleChoiceUserQuizQuestion') {
                accumulator[currentValue._id.$oid] = currentValue.studentAnswers ?
                    Array.from(new Set(currentValue.studentAnswers.map(answer => answer._id.$oid))) : [];
            } else if (currentValue._cls === 'SingleChoiceUserQuizQuestion') {
                accumulator[currentValue._id.$oid] = currentValue.studentAnswer ?
                    [currentValue.studentAnswer._id.$oid] : [];
            } else {
                throw Error('Unexpected quiz type.');
            }
            return accumulator;
        }, {});


export const putFileToAWSS3 = (data, body) => {
    const headers = {
        method: 'PUT',
        mode: 'cors',
        body,
    };
    if (data.mimetype) {
        headers['headers'] = {
            'Content-Type': data.mimetype,
        };
    }
    return fetch(
        data.url,
        headers
    ).then(() => {
        return data;
    });
};

export const fileUploadPromise = (fileList, {isProtected = false}) => {
    const fileNameList = fileList.reduce((accumulator, file) => {
        accumulator.push(file.name);
        return accumulator;
    }, []);
    return request.postJSON(
        `/s3/presigned-urls?protected=${isProtected}`,
        {keys: fileNameList}
    ).then(fileData => {
        const promiseUploadList = [];
        for (const fileKey in fileData) {
            const file = fileList.find(file => toCamelCase(file.name) === fileKey);
            if (!file) {
                throw Error('Unexpected error: failed finding file ' + fileKey);
            }
            promiseUploadList.push(
                putFileToAWSS3(
                    {
                        name: fileKey,
                        type: file.type,
                        size: file.size,
                        ...fileData[fileKey]
                    }, file
                )
            );
        }
        return Promise.all(promiseUploadList);
    });
};

export const formatProtectedAWSURL = (s3SignedResponse) => {
    if (isEmpty(s3SignedResponse)) {
        return '';
    }
    const urlIndex = s3SignedResponse.url.indexOf('?');
    if (urlIndex !== -1) {
        return s3SignedResponse.cloudfrontUrl.substr(0, urlIndex);
    }
    return '';
};

export const baseFilename = (url) => {
    if (isEmpty(url)) {
        throw Error('URL provided cannot be empty.');
    }
    return url.substring(url.lastIndexOf('/') + 1);
};

export const filterNodeProperties = function (node, keys, defaultData = {}) {
    return keys.reduce(
        (data, key) => {
            return {...data, [key]: node[key]};
        }, defaultData
    );
};
